'use client';

import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/navbar';
import { Footer } from '../components/layout/footer';
import { Button } from '../components/ui/button';
import { Check, Star } from 'lucide-react';

const plans = [
    {
        name: 'Starter',
        price: '$20',
        desc: 'A great place to start a community',
        features: ['Unlimited members', '2 staff', 'talktrail.group domain'],
        highlight: false,
    },
    {
        name: 'Pro',
        price: '$100',
        desc: 'Connect with your community and grow together',
        features: ['Unlimited members', '5 staff', 'Custom domain', '16 plugins', 'Custom themes', 'API Access'],
        highlight: true,
    },
    {
        name: 'Business',
        price: '$500',
        desc: 'Scale your business and help customers connect',
        features: ['Unlimited members', '15 staff', 'Custom domain', '26 plugins', 'Priority support', 'Migration services'],
        highlight: false,
    },
    {
        name: 'Enterprise',
        price: 'Custom',
        desc: 'Tailored to your needs and brand',
        features: ['Unlimited members', 'Unlimited staff', '50+ plugins', 'Design services', 'Custom development', 'SLA Support'],
        highlight: false,
    },
];

export default function PricingPage() {
    return (
        <div className="flex min-h-screen flex-col bg-black text-white selection:bg-violet-500/30">
            <div className="fixed inset-0 z-0 pointer-events-none">
                <div className="absolute bottom-0 left-[20%] w-[60%] h-[40%] rounded-full bg-violet-900/10 blur-[120px]" />
            </div>

            <Navbar />

            <main className="flex-1 relative z-10 pt-24 pb-20">
                <div className="container px-4 md:px-6 mb-16 text-center">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6">
                        Talktrail hosting plans
                    </h1>
                    <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
                        Let us handle the technical details while you focus on your community.
                        All prices in USD. No credit card required up front.
                    </p>
                </div>

                <div className="container px-4 md:px-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
                    {plans.map((plan, i) => (
                        <motion.div
                            key={plan.name}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className={`relative flex flex-col p-6 rounded-2xl border ${plan.highlight
                                    ? 'border-violet-500 bg-violet-900/10 shadow-[0_0_30px_-5px_rgba(124,58,237,0.3)]'
                                    : 'border-white/10 bg-zinc-900/30'
                                }`}
                        >
                            {plan.highlight && (
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 rounded-full bg-violet-500 text-xs font-bold uppercase tracking-wider">
                                    Most Popular
                                </div>
                            )}

                            <div className="mb-6">
                                <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-4xl font-bold">{plan.price}</span>
                                    {plan.price !== 'Custom' && <span className="text-zinc-500">/mo</span>}
                                </div>
                                <p className="text-sm text-zinc-400 mt-3">{plan.desc}</p>
                            </div>

                            <div className="space-y-4 mb-8 flex-1">
                                {plan.features.map((feature) => (
                                    <div key={feature} className="flex items-start gap-3 text-sm">
                                        <Check className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
                                        <span className="text-zinc-300">{feature}</span>
                                    </div>
                                ))}
                            </div>

                            <Button
                                variant={plan.highlight ? 'default' : 'outline'}
                                className={`w-full rounded-xl h-12 font-bold ${plan.highlight
                                        ? 'bg-white text-black hover:bg-zinc-200'
                                        : 'border-white/20 hover:bg-white/10'
                                    }`}
                            >
                                {plan.price === 'Custom' ? 'Contact Sales' : 'Start free 14-day trial'}
                            </Button>
                        </motion.div>
                    ))}
                </div>

                <div className="container px-4 md:px-6 mt-20 text-center">
                    <p className="text-zinc-500 mb-4">
                        Prefer to self-host and handle installation, upgrades, and maintenance yourself?
                    </p>
                    <p className="text-zinc-400">
                        You can install our free open-source Talktrail software from GitHub.
                    </p>
                </div>
            </main>

            <Footer />
        </div>
    );
}
